import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Box } from '@lightspeed/flame/Core';
import { Heading2 } from '@lightspeed/flame/Text';

import { ErrorMessageContext } from 'root/apps/PurchaseOrder/ShipmentHistory/contexts/ErrorMessage';
import { getApiError } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/api-error-codes';

import Information from './components/Information';
import Overview from './components/Overview';
import Shipments from './components/Shipments';

import type { Page } from '..';
import type { ShipmentsOverview } from 'root/apps/PurchaseOrder/ShipmentHistory/types';

interface Props {
  setPage: (page: Page) => void;
}

const ShipmentHistoryListing: React.FC<Props> = ({ setPage }) => {
  const [overviewData, setOverviewData] = React.useState<ShipmentsOverview>();
  const [isOverviewLoading, setIsOverviewLoading] = React.useState(true);

  const { id: orderId } = window.history.getFilters();
  const { currency_code: currencyCode } = window.merchantos.locale;
  const isMultiCurrency = overviewData
    ? overviewData?.currentVendorCurrencyCode !== currencyCode
    : false;
  const { error, setError } = React.useContext(ErrorMessageContext);

  const getOverviewData = async () => {
    setIsOverviewLoading(true);
    try {
      const res = await fetch(`/admin/order/${orderId}/overview`);
      if (res.status !== 200) {
        const { title, message } = getApiError('GENERIC_GET');
        throw new Error(title || message);
      }
      const { data } = await res.json();
      setOverviewData(data);
    } catch (err) {
      setError(getApiError('GENERIC_GET'));
    }
    setIsOverviewLoading(false);
  };

  React.useEffect(() => {
    setError(null);
    getOverviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box p={4} backgroundColor="gray-50" style={{ fontSize: '14px' }}>
      {error ? (
        <Alert type="danger" title={error.title} noCloseBtn data-test="api-error-alert" mb={2}>
          {error.message}
        </Alert>
      ) : null}
      {!isOverviewLoading ? (
        <Heading2 py={0} style={{ fontSize: '21px' }}>
          Shipment history for PO <var>#{overviewData?.orderID || ''}</var>
        </Heading2>
      ) : (
        <Box>
          <Bone height={4} width="30%" />
        </Box>
      )}
      <Information isLoading={isOverviewLoading} data={overviewData} />
      <Overview
        isLoading={isOverviewLoading}
        data={overviewData}
        isMultiCurrency={isMultiCurrency}
      />
      <Shipments
        isMultiCurrency={isMultiCurrency}
        vendorCurrencyCode={overviewData?.currentVendorCurrencyCode}
        setPage={setPage}
      />
    </Box>
  );
};

export default ShipmentHistoryListing;
